import { ChangeDetectorRef, Component, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { SafeStorageService } from '../../shared/safe-storage.service';
import { CarrinhoService } from '../../services/carrinho.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { CotacaoService } from '../../services/cotacao/cotacao.service';
import { Subscription } from 'rxjs';
import { LanguageService, SupportedLang } from '../../shared/language.service';
import { ThemeService } from '../../shared/theme.service';
import {
  PedidosPendentesService,
  PedidoPendenteResumo,
} from '../../services/pedidos/pedidos-pendentes.service';
import { TranslateService } from '@ngx-translate/core';
import { SessaoService } from '../../services/sessao/sessao.service';
import { resolverTipoEntrega, TIPO_ENTREGA_CONFIG } from '../../enums/TipoDeEntrega.enum';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  opcoes: any[];
  selectedOption: any;

  cartCount = 0;
  private sub?: Subscription;
  /** Guarda para nao reconciliar a view depois que o header ja saiu da tela. */
  private destruido = false;

  // Pedidos pendentes (estado ABERTO) — badge e popover no header
  pedidosPendentes: PedidoPendenteResumo[] = [];
  showPedidosPendentes = false;
  private pedidosSub?: Subscription;

  // LANG
  current!: SupportedLang;         // 'pt-BR' | 'es' (ou 'es-ES', serviço normaliza)
  currentCode: string = 'pt-BR';   // vinculado ao [(ngModel)] do dropdown

  langs = [
    { code: 'pt-BR', label: 'Português', flag: 'assets/images/br-flag.svg' },
    { code: 'es-ES', label: 'Español',   flag: 'assets/images/paraguay-flag.jpg' },
  ];

  // Menu mobile
  showMobileMenu = false;

  // FIX-S2: Versão do app
  appVersion = 'v1.0.0';

  // ATALHOS - Novo registro
  showNovoRegistro = false;

  atalhos = [
    { labelKey: 'header.atalhos.nova_venda',         icon: 'pi pi-cart-plus',               route: '/vendas',            queryParams: {} },
    { labelKey: 'header.atalhos.nova_compra',         icon: 'pi pi-shopping-cart',            route: '/adicionar-compra',  queryParams: {} },
    { labelKey: 'header.atalhos.novo_cliente',        icon: 'pi pi-user-plus',               route: '/clientes',          queryParams: { aba: 'cadastro' } },
    { labelKey: 'header.atalhos.novo_produto',        icon: 'pi pi-box',                     route: '/produtos',          queryParams: { aba: 'cadastro' } },
    { labelKey: 'header.atalhos.nova_despesa',        icon: 'pi pi-wallet',                  route: '/nova-despesa',       queryParams: { aba: 'cadastro' } },
    { labelKey: 'header.atalhos.nova_receita',        icon: 'pi pi-dollar',                  route: '/nova-receita',       queryParams: { aba: 'cadastro' } },
    { labelKey: 'header.atalhos.nova_transferencia',  icon: 'pi pi-arrow-right-arrow-left',  route: '/nova-transferencia', queryParams: { aba: 'cadastro' } },
  ];

  get currentFlag(): string {
    return this.langs.find(l => l.code === this.currentCode)?.flag || this.langs[0].flag;
  }
  get currentLabel(): string {
    return this.langs.find(l => l.code === this.currentCode)?.label || this.langs[0].label;
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private safe: SafeStorageService,
    private carrinhoService: CarrinhoService,
    private empresaConfigService: EmpresaConfigService,
    private cotacaoService: CotacaoService,
    private langService: LanguageService,
    public themeService: ThemeService,
    private elementRef: ElementRef,
    private cdRef: ChangeDetectorRef,
    private pedidosPendentesService: PedidosPendentesService,
    private translate: TranslateService,
    private sessaoService: SessaoService,
  ) {
    this.opcoes = [
      { label: 'Vendas', value: 'Vendas' },
      { label: 'Sair', value: 'Sair' },
    ];
  }

  ngOnInit(): void {
    // carrinho
    this.sub = this.carrinhoService.count$.subscribe((qtd) => {
      this.cartCount = qtd;
      // O header e checado ANTES da pagina: quando a tela de Vendas restaura o carrinho no
      // proprio ngOnInit (ou grava o cache no meio do ciclo), o novo valor chega depois de o
      // badge ja ter sido verificado — e o modo de desenvolvimento acusa NG0100. Reconciliar
      // aqui fecha o ciclo com o valor certo, em vez de deixar a diferenca para a checagem
      // seguinte.
      if (!this.destruido) this.cdRef.detectChanges();
    });
    this.carrinhoService.refreshFromCache();

    // pedidos pendentes (mini shop)
    this.pedidosSub = this.pedidosPendentesService.pedidos$.subscribe(list => {
      this.pedidosPendentes = list;
    });

    // idioma atual
    this.current = this.langService.getCurrent(); // ex: 'pt-BR' ou 'es'
    // normaliza para o que existe no array de options
    this.currentCode = (this.current === 'es' ? 'es-ES' : this.current) as string;
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.sub?.unsubscribe();
    this.pedidosSub?.unsubscribe();
  }

  get pedidosPendentesCount(): number {
    return this.pedidosPendentes.length;
  }

  setLang(langCode: string): void {
    this.langService.setLanguage(langCode);     // aceita 'pt', 'pt-BR', 'es', 'es-ES'...
    this.current = this.langService.getCurrent();
    this.currentCode = (this.current === 'es' ? 'es-ES' : this.current) as string;
  }

  goToVendas() {
    this.router.navigate(['/vendas']);
  }

  logout() {
    // Libera a vaga de sessao WEB antes de descartar o token — sem isso o usuario que sai
    // continuaria ocupando a vaga ate o backend considerar a sessao abandonada por inatividade.
    // A chamada nunca falha para o usuario: o service engole o erro.
    this.sessaoService.encerrar().subscribe();

    this.safe.removeItem('authToken');
    this.safe.removeItem('id');
    // Invalida cache da sessao da empresa para nao vazar dados entre usuarios
    this.empresaConfigService.clearSessaoCache();
    // Invalida cache da ultima cotacao para nao vazar dados entre usuarios
    this.cotacaoService.clearCotacaoCache();
    this.authService.setAuthenticated(false);
    this.router.navigateByUrl('/login');
  }

  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  closeMobileMenu() {
    this.showMobileMenu = false;
  }

  // FIX-S6: Fecha sidebar antes de executar logout
  mobileLogout() {
    this.closeMobileMenu();
    this.logout();
  }

  /** Fecha paineis (atalhos e pedidos pendentes) ao clicar fora */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showNovoRegistro) {
      const wrapper = this.elementRef.nativeElement.querySelector('.novo-registro-wrapper');
      if (wrapper && !wrapper.contains(event.target)) {
        this.showNovoRegistro = false;
      }
    }
    if (this.showPedidosPendentes) {
      const wrapper = this.elementRef.nativeElement.querySelector('.pedidos-pendentes-wrapper');
      if (wrapper && !wrapper.contains(event.target)) {
        this.showPedidosPendentes = false;
      }
    }
  }

  togglePedidosPendentes(event: MouseEvent): void {
    event.stopPropagation();
    this.showPedidosPendentes = !this.showPedidosPendentes;
    if (this.showPedidosPendentes) {
      // Refresca a lista ao abrir para sincronizar com o backend
      this.pedidosPendentesService.recarregar();
    }
  }

  verPedidoPendente(pedido: PedidoPendenteResumo, event: MouseEvent): void {
    event.stopPropagation();
    this.showPedidosPendentes = false;
    // Nao marca como visto localmente: o badge so sai de pendente quando o
    // estado muda no backend (ex.: enviar para producao, cancelar).
    this.router.navigate(['/pedidos'], { state: { abrirPedidoId: pedido.id } });
  }

  verTodosPedidosPendentes(event: MouseEvent): void {
    event.stopPropagation();
    this.showPedidosPendentes = false;
    this.router.navigate(['/pedidos']);
  }

  toggleNovoRegistro(event: MouseEvent) {
    event.stopPropagation();
    this.showNovoRegistro = !this.showNovoRegistro;
  }

  navigateAtalho(atalho: { route: string; queryParams: Record<string, string | undefined> }, event: MouseEvent) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.showNovoRegistro = false;
    this.router.navigate([atalho.route], { queryParams: atalho.queryParams });
  }

  onOptionSelect() {
    if (this.selectedOption?.value === 'Sair') this.logout();
    else if (this.selectedOption?.value === 'Vendas') this.router.navigateByUrl('/vendas');
  }

  /**
   * Retorna um rótulo relativo amigável para a data/hora do pedido.
   * Ex.: "Agora", "há 5 min", "há 2 h", "Hoje, 14:35", "Ontem, 14:35",
   *      ou "dd/MM HH:mm" para datas mais antigas.
   */
  formatarTempoRelativo(criadoEm: string): string {
    if (!criadoEm) return '';

    const data = new Date(criadoEm);
    if (isNaN(data.getTime())) return '';

    const agora = new Date();
    const diffMs = agora.getTime() - data.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) {
      return this.translate.instant('header.pedidos_pendentes.tempo.agora');
    }
    if (diffMin === 1) {
      return this.translate.instant('header.pedidos_pendentes.tempo.ha_minuto');
    }
    if (diffMin < 60) {
      return this.translate.instant('header.pedidos_pendentes.tempo.ha_minutos', { n: diffMin });
    }

    const hora = this.formatarHora(data);
    const mesmoDia = data.toDateString() === agora.toDateString();
    if (mesmoDia) {
      const diffH = Math.floor(diffMin / 60);
      if (diffH === 1) {
        return this.translate.instant('header.pedidos_pendentes.tempo.ha_hora');
      }
      if (diffH < 6) {
        return this.translate.instant('header.pedidos_pendentes.tempo.ha_horas', { n: diffH });
      }
      return this.translate.instant('header.pedidos_pendentes.tempo.hoje_as', { hora });
    }

    const ontem = new Date(agora);
    ontem.setDate(ontem.getDate() - 1);
    if (data.toDateString() === ontem.toDateString()) {
      return this.translate.instant('header.pedidos_pendentes.tempo.ontem_as', { hora });
    }

    // Datas mais antigas: dd/MM HH:mm
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    return `${dia}/${mes} ${hora}`;
  }

  /** Destaca visualmente pedidos muito recentes (últimos 5 min). */
  ehPedidoNovo(criadoEm: string): boolean {
    if (!criadoEm) return false;
    const data = new Date(criadoEm);
    if (isNaN(data.getTime())) return false;
    const diffMin = (Date.now() - data.getTime()) / 60000;
    return diffMin < 5;
  }

  private formatarHora(data: Date): string {
    const h = String(data.getHours()).padStart(2, '0');
    const m = String(data.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  // =========================================================================
  // Badge de tipo de entrega (Retirada / Delivery) no popover
  // =========================================================================

  temTipoEntrega(tipo?: string): boolean {
    return resolverTipoEntrega(tipo) !== null;
  }

  getTipoEntregaLabel(tipo?: string): string {
    if (!tipo) return '';
    // Usa a mesma chave i18n da tela de pedidos para manter consistencia
    return this.translate.instant('pedidos.tipo_entrega.' + tipo);
  }

  getTipoEntregaIcone(tipo?: string): string {
    const tipoResolvido = resolverTipoEntrega(tipo);
    return tipoResolvido ? TIPO_ENTREGA_CONFIG[tipoResolvido].icone : 'pi pi-tag';
  }

  getTipoEntregaClasse(tipo?: string): string {
    const tipoResolvido = resolverTipoEntrega(tipo);
    return tipoResolvido ? TIPO_ENTREGA_CONFIG[tipoResolvido].classe : '';
  }
}
